import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { Plus, X, Users as UsersIcon, Edit2, Trash2, Shield, User } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '' });

  const showAlert = (message) => {
    setModalConfig({
      isOpen: true,
      title: 'Notification',
      message,
      confirmLabel: 'OK',
      confirmVariant: 'primary',
      onConfirm: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
      onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
    });
  };

  const showConfirm = (message, onConfirmCallback) => {
    setModalConfig({
      isOpen: true,
      title: 'Confirm Action',
      message,
      confirmLabel: 'Confirm',
      confirmVariant: 'danger',
      onConfirm: () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
        onConfirmCallback();
      },
      onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
    });
  };

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'sales_agent',
    full_name: '',
    email: '',
    is_active: true,
    on_vacation: false,
    auto_assign_enabled: true
  });

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      showAlert('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.username || (!editingUser && !formData.password) || !formData.role) {
      showAlert('Please fill in required fields');
      return;
    }

    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, formData);
        showAlert('User updated successfully');
      } else {
        await usersAPI.create(formData);
        showAlert('User created successfully');
      }
      
      setFormData({
        username: '',
        password: '',
        role: 'sales_agent',
        full_name: '',
        email: '',
        is_active: true,
        on_vacation: false,
        auto_assign_enabled: true
      });
      setShowAddForm(false);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      showAlert(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      full_name: user.full_name || '',
      email: user.email || '',
      is_active: user.is_active ?? true,
      on_vacation: user.on_vacation ?? false,
      auto_assign_enabled: user.auto_assign_enabled ?? true
    });
    setShowAddForm(true);
  };

  const handleDelete = (userId) => {
    showConfirm('Delete this user? This action cannot be undone.', async () => {
      try {
        await usersAPI.delete(userId);
        loadUsers();
        showAlert('User deleted successfully');
      } catch (error) {
        console.error('Failed to delete user:', error);
        showAlert(error.response?.data?.error || 'Failed to delete user');
      }
    });
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'sales_agent',
      full_name: '',
      email: '',
      is_active: true,
      on_vacation: false,
      auto_assign_enabled: true
    });
    setShowAddForm(false);
    setEditingUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h1 className="app-page-title">User Management</h1>
          <p className="app-page-subtitle">Manage system users and permissions</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Add User
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>
            <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Username *"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="ui-control"
              disabled={editingUser}
            />
            <input
              type="password"
              placeholder={editingUser ? "Password (leave blank to keep current)" : "Password *"}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="ui-control"
            />
            <input
              type="text"
              placeholder="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="ui-control"
            />
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="ui-control"
            />
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="ui-control"
            >
              <option value="editor">Editor</option>
              <option value="sales_agent">Sales Agent</option>
              <option value="admin">Admin (Full Access)</option>
            </select>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
              <span className="text-sm font-medium text-gray-700">Account is Active</span>
            </label>
            {formData.role === 'sales_agent' && (
              <>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.on_vacation} onChange={(e) => setFormData({...formData, on_vacation: e.target.checked})} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">On Vacation (Do not assign leads)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.auto_assign_enabled} onChange={(e) => setFormData({...formData, auto_assign_enabled: e.target.checked})} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Eligible for Auto-Assignment</span>
                </label>
              </>
            )}
          </div>
          <div className="flex gap-2 mt-6 flex-wrap">
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              {editingUser ? 'Update User' : 'Create User'}
            </button>
            <button
              onClick={resetForm}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${user.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {user.role === 'admin' ? (
                          <Shield className={`${user.role === 'admin' ? 'text-purple-600' : 'text-blue-600'}`} size={20} />
                        ) : (
                          <User className="text-blue-600" size={20} />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{user.full_name || user.username}</p>
                        <p className="text-sm text-gray-600">@{user.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{user.email || 'No email'}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'sales_agent' ? 'Sales Agent' : 'Editor'}
                    </span>
                    {!user.is_active && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Inactive</span>
                    )}
                    {user.on_vacation && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Vacation</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">
                      {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No users yet</h3>
            <p className="text-gray-600 mb-4">Add your first user to get started</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Add User
            </button>
          </div>
        )}
      </div>

      <ConfirmModal {...modalConfig} />
    </div>
  );
};

export default Users;